import { motion } from "framer-motion";
import { CheckCircle } from "@phosphor-icons/react";
import { itemAnim } from "./constants";

// ----- Helpers -----

export function Section({ icon, title, intro, children }) {
  return (
    <motion.div
      variants={itemAnim}
      className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
    >
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h2 className="font-['Outfit'] text-white text-xl font-medium">{title}</h2>
      </div>
      {intro && <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{intro}</p>}
      {children}
    </motion.div>
  );
}

export function BulletList({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((t, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
          <span className="text-[#A1A1AA] text-sm leading-relaxed">{t}</span>
        </li>
      ))}
    </ul>
  );
}

export function CardGrid({ cards }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map((card, i) => (
        <div
          key={i}
          className="bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-colors"
          data-testid={`studio-card-${i}`}
        >
          <div className="relative h-36 bg-[#18181B]">
            <img
              src={card.img}
              alt={card.name}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 to-transparent" />
          </div>
          <div className="p-4">
            <h3 className="text-white font-medium text-sm mb-1">{card.name}</h3>
            <p className="text-[#A1A1AA] text-xs leading-relaxed mb-2">{card.desc}</p>
            <p className="text-[#F5A524] text-xs font-['JetBrains_Mono']">{card.price}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ChecklistCard({ title, items, highlight = false }) {
  return (
    <div
      className={`rounded-xl p-5 border ${
        highlight
          ? "bg-[#F5A524]/5 border-[#F5A524]/30"
          : "bg-[#0A0A0A] border-white/10"
      }`}
    >
      <h3 className={`font-['Outfit'] font-medium mb-3 text-base ${highlight ? "text-[#F5A524]" : "text-white"}`}>
        {title}
      </h3>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-[#A1A1AA] text-sm">
            <CheckCircle weight="fill" className="w-3.5 h-3.5 text-[#F5A524] mt-0.5 shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PrintSection({ title, intro, items, children }) {
  return (
    <div className="mb-6 break-inside-avoid">
      <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">{title}</h2>
      {intro && <p className="text-sm text-gray-700 mb-3">{intro}</p>}
      {items && (
        <ul className="list-disc ml-5 space-y-1">
          {items.map((t, i) => (
            <li key={i} className="text-xs text-gray-700 leading-relaxed">
              {t}
            </li>
          ))}
        </ul>
      )}
      {children}
    </div>
  );
}

export function PrintCard({ card }) {
  return (
    <div className="mb-2">
      <p className="text-sm">
        <strong>{card.name}</strong>
        <span className="text-gray-500"> — {card.price}</span>
      </p>
      <p className="text-xs text-gray-700 leading-relaxed">{card.desc}</p>
    </div>
  );
}
